<template>
  <section class="engine" name="engine">
    <i v-for="item in 3"
       :class="{i_active:item===tab.active}" />
    <div class="container">
      <a-typography-title :level="1"
                          data-aos="fade-down">
        Three Self-developed Engines Underpinning Our Core Capabilities
      </a-typography-title>
      <a-tabs v-model:activeKey="tab.active"
              data-aos="fade-up" centered>
        <a-tab-pane v-for="(item,index) in tab.list"
                    :key="index+1" :tab="item.title">
          <a-row :class="{tab_show:tab.show===index+1}">
            <a-col :xs="24" :lg="12">
              <div class="item" :class="`i_${index+1}`">
                <a-typography-title v-if="item.sub" :level="4">
                  {{ item.title }}
                </a-typography-title>
                <a-typography-title v-if="item.sub" :level="5">
                  {{ item.sub }}
                </a-typography-title>
                <div class="line" />
                <a-typography-paragraph>
                  <p>{{ item.text_sub }}</p>
                  <span v-for="v in item.text">
                    {{ v }}
                  </span>
                </a-typography-paragraph>
              </div>
            </a-col>
            <a-col :xs="24" :lg="12">
              <div class="media" :class="`m_${index+1}`" />
            </a-col>
          </a-row>
        </a-tab-pane>
      </a-tabs>
    </div>
  </section>
</template>

<script lang="ts" type="text/tsx">
import { DataInterface } from '@interface/public';

export default {
  name: 'home_engine',
  data(): DataInterface {
    return {
      tab: {
        active: 1,
        show: 1,
        list: [
          {
            title: 'BridgX',
            sub: 'Computing resources scheduling engine',
            text_sub: 'Schedules computing resources at the following levels:',
            text: [
              'Schedules resources across multiple clouds and hybrid clouds',
              'Schedules containers of different specifications by using Kubernetes and other technologies',
              'Schedules workloads, for example, by deploying online and offline workloads in the same cluster or performing peak-load shifting'
            ]
          },
          {
            title: 'DTExpress',
            sub: 'Data express engine',
            text_sub: 'Quickly transmits large amounts of data at an extremely low cost without using leased lines.',
            text: [
              'Transmits data across different regions on different clouds',
              'Enables fast recovery of data, code, and images across IDCs or clouds',
              'Transmits data between edge clouds and center clouds across different cloud vendors'
            ]
          },
          {
            title: 'CudgX',
            sub: 'Metric monitoring and measuring engine',
            text_sub: 'Provides precise measuring and monitoring of service metrics to support the following capabilities that help improve your O&M efficiency:',
            text: [
              'Auto pressure tests',
              'Log recording',
              'Real-time cluster capacity estimation'
            ]
          }
        ]
      },
    };
  },
  watch: {
    'tab.active': {
      async handler(newData: any, oldDate: any): Promise<any> {
        await $FN.WaitTime(50);
        this.tab.show = newData;
      }
    }
  }
}
</script>

<style lang="less" rel="stylesheet/less" scoped>
@import '@resource/css/_global.less';

@image: '@{src_image}overseas/home/';
@c_1: #FFEA8E;

.engine {
  overflow: hidden;
  position: relative;
  padding: 0 @padding;
  
  i {
    display: block;
    .position();
    z-index: 0;
    opacity: 0;
    background-size: cover !important;
    .transition(opacity @time);
    
    .loop(@i) when (@i<=3) {
      &:nth-child(@{i}) {
        .bg('@{image}engine_bg_@{i}.jpg');
      }
      .loop(@i+1);
    }
    .loop(1);
    
    &.i_active {
      opacity: 1;
    }
  }
  
  .container {
    position: relative;
    z-index: 1;
    max-width: @grid_xl;
    padding: @space 0 0;
    margin: 0 auto;
    .borderBox();
    text-align: center;
  }
}

h1.ant-typography {
  margin: 0;
  .font(center, @title, @color_white, @title, true);
}

.ant-tabs {
  overflow: initial;
  margin: @margin_mo 0 0;
  
  &:deep(.ant-tabs-nav) {
    &::before {
      display: none;
    }
    
    .ant-tabs-nav-list {
      position: relative;
      display: flex;
      width: 100%;
      
      &::before {
        content: '';
        display: block;
        .position(absolute, auto, 0, 1px, 50%);
        z-index: 0;
        width: 200%;
        border-bottom: 1px solid @color_white;
        .transform(translateX(-50%) scale(0.5));
      }
      
      .ant-tabs-tab {
        flex: 1;
        
        .ant-tabs-tab-btn {
          margin: 0 auto;
        }
      }
      
      .ant-tabs-ink-bar {
        height: 3px;
      }
    }
    
    .ant-tabs-tab {
      padding: 20px 0;
      .font(center, @text_big, @color_white, @text_big, true);
      
      &.ant-tabs-tab-active {
        .ant-tabs-tab-btn {
          color: @c_1;
        }
      }
    }
    
    .ant-tabs-ink-bar {
      background-color: @c_1;
    }
  }
  
  &:deep(.ant-tabs-content) {
    .ant-tabs-tabpane {
      .tab_show {
        .item,
        .media {
          opacity: 1;
          .transform(translate(0, 0));
        }
      }
      
      .item {
        .transform(translate(-100px, 0));
      }
      
      .media {
        .transform(translate(100px, 0));
      }
      
      .item,
      .media {
        @aTime: 1s;
        @style: transform @aTime, opacity @aTime;
        opacity: 0;
        .transition(@style);
      }
    }
  }
}

.ant-row {
  .ant-col {
    position: relative;
    
    &:nth-child(1) {
      z-index: 1;
    }
    
    &:nth-child(2) {
      z-index: 0;
    }
  }
}

.item {
  .position();
  height: 300px;
  margin: auto;
  
  .line {
    margin: @margin_mo 0 0;
    .bg('@{image}engine_line.png', 100%, 3px, true);
  }
  
  h4.ant-typography {
    margin: 0;
    .font(left, @title_sub, @color_white, @title_sub, true);
  }
  
  h5.ant-typography {
    margin: @margin_mo 0 0;
    .font(left, @text_big, @color_white, @text_big, true);
  }
  
  div.ant-typography {
    margin: @margin_mo 0 0;
    .font(left, @text+@line, @color_white, @text);
    
    &:nth-child(2) {
      margin: 20px 0 0;
    }
    
    p {
      margin: @margin_mo 0 0;
    }
    
    span {
      display: block;
      position: relative;
      padding-left: 15px;
      margin: 5px 0 0;
      
      &::before {
        content: '';
        display: block;
        .position(absolute, 10px, auto, auto, 0);
        width: 5px;
        height: 5px;
        margin: auto 0;
        border-radius: 4px;
        background-color: @color_white;
      }
    }
  }
}

.media {
  position: relative;
  height: 400px;
  
  &.m_1 {
    &::before {
      left: -100px;
      bottom: -40px;
      .bg('@{image}engine_bottom_1.png', 1071px, 433px, true);
    }
    
    &::after {
      left: 20px;
      bottom: -50px;
      .bg('@{image}engine_icon_1.png', 624px, 397px, true);
    }
  }
  
  &.m_2 {
    &::before {
      left: -100px;
      bottom: -50px;
      .bg('@{image}engine_bottom_2.png', 859px, 444px, true);
    }
    
    &::after {
      left: 40px;
      bottom: -10px;
      .bg('@{image}engine_icon_2.png', 565px, 373px, true);
    }
  }
  
  &.m_3 {
    &::before {
      left: -100px;
      bottom: -30px;
      .bg('@{image}engine_bottom_3.png', 797px, 418px, true);
    }
    
    &::after {
      left: 0;
      bottom: -20px;
      .bg('@{image}engine_icon_3.png', 582px, 389px, true);
    }
  }
  
  &::before,
  &::after {
    content: '';
    display: block;
    position: absolute;
  }
  
  &::before {
    .transform(translate(calc(~'var(--mouse-x,0px) * -0.02'), calc(~'var(--mouse-y,0px) * -0.02')));
  }
  
  &::after {
    .transform(translate(calc(~'var(--mouse-x,0px) * -0.01'), calc(~'var(--mouse-y,0px) * -0.01')));
  }
}

@media screen and (max-width: -1px+@grid_lg) {
  .ant-tabs {
    &:deep(.ant-tabs-content) {
      .ant-tabs-tabpane {
        .item {
          @aTime: 1s;
          @dTime: 0.2s;
          @style: transform @aTime @dTime, opacity @aTime @dTime;
          .transform(translate(0, 50px));
          .transition(@style);
        }
      }
    }
  }
  
  .item {
    position: relative;
    height: auto;
    text-align: center;
  }
  
  .media {
    @scale: 0.5;
    width: 300px;
    height: 200px;
    margin: 0 auto;
    
    &.m_1 {
      &::before {
        left: -100px*@scale;
        bottom: -40px*@scale;
        .bg('@{image}engine_bottom_1.png', 1071px*@scale, 433px*@scale, true);
      }
      
      &::after {
        left: 20px*@scale;
        bottom: -50px*@scale;
        .bg('@{image}engine_icon_1.png', 624px*@scale, 397px*@scale, true);
      }
    }
    
    &.m_2 {
      &::before {
        left: -100px*@scale;
        bottom: -50px*@scale;
        .bg('@{image}engine_bottom_2.png', 859px*@scale, 444px*@scale, true);
      }
      
      &::after {
        left: 40px*@scale;
        bottom: -10px*@scale;
        .bg('@{image}engine_icon_2.png', 565px*@scale, 373px*@scale, true);
      }
    }
    
    &.m_3 {
      &::before {
        left: -100px*@scale;
        bottom: -30px*@scale;
        .bg('@{image}engine_bottom_3.png', 797px*@scale, 418px*@scale, true);
      }
      
      &::after {
        left: 0*@scale;
        bottom: -20px*@scale;
        .bg('@{image}engine_icon_3.png', 582px*@scale, 389px*@scale, true);
      }
    }
  }
}

@media screen and (max-width: -1px+@grid_md) {
  .engine {
    .container {
      padding: @space_mo 0 0;
    }
  }
  
  h1.ant-typography {
    .font(center, @title_mo+@line_mo, @color_white, @title_mo, true);
  }
  
  .ant-tabs {
    &:deep(.ant-tabs-nav) {
      .ant-tabs-tab {
        padding: 10px 0;
        .font(center, @font_7+@line, @color_white, @font_7, true);
        
        &.ant-tabs-tab-active {
          .ant-tabs-tab-btn {
            color: @c_1;
          }
        }
      }
    }
  }
  
  .item {
    max-width: 350px;
    min-height: 400px;
    padding: @margin_mo 0 0;
    
    h4.ant-typography {
      .font(left, @title_sub_mo, @color_white, @title_sub_mo, true);
    }
    
    h5.ant-typography {
      .font(left, @text_big_mo+@line_mo, @color_white, @text_big_mo, true);
    }
    
    div.ant-typography {
      .font(left, @text_mo+@line_mo, @color_white, @text_mo);
    }
  }
}
</style>